import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import './FAQ.css';

export const faqData = [
  {
    question: "I'm not very tech-savvy. Can I still use this?",
    answer: "Absolutely — that's exactly who we built this for. You don't need to download any apps, learn any software, or change how you run your business. We handle all the setup and tech. You just keep doing what you do, and the AI works quietly in the background answering calls, booking appointments, and following up with customers. If you can answer a phone call, you can use Ubotika."
  },
  {
    question: "What types of businesses do you work with?",
    answer: "We work mostly with local businesses in Los Angeles — restaurants, dental offices, doctor's offices, real estate agents, law firms, salons, and repair shops. If your business gets phone calls and books appointments, we can help. Our busiest clients are in restaurants, healthcare, and real estate, and most of them see the system pay for itself within 90 days."
  },
  {
    question: "How long does it take to get set up?",
    answer: "Most businesses are up and running in 1 to 2 weeks. For bigger setups — like connecting to your booking system or customer database — it might take 3 to 4 weeks. Either way, we do all the work. You just tell us how you want calls handled, and we take it from there."
  },
  {
    question: "How much money will this actually save me?",
    answer: "On average, our clients save $17,000 a year — mostly from cutting phone staffing costs by 70%. One LA restaurant owner was paying someone $22,000 a year just to answer the phone. Now the AI handles it for a fraction of the cost, and it never calls in sick. Use our ROI Calculator above to see what you could save."
  },
  {
    question: "What happens when someone calls my business?",
    answer: "The AI answers in under 2 seconds — no ringing, no hold music, no voicemail. It sounds natural and friendly, like talking to a real person. It can answer common questions (hours, location, pricing), book appointments, take messages, and transfer to you or your staff when needed. It handles over 5,000 calls a month for our average client, and 94% of callers say they're happy with the experience."
  },
  {
    question: "Is my customer information safe?",
    answer: "Yes, 100%. We take security very seriously. All your data is encrypted and stored securely. We follow strict industry standards to protect your information, and we never share or sell your customer data to anyone. Your business data stays your business data."
  },
  {
    question: "Will this work with the systems I already use?",
    answer: "Most likely, yes. We connect with over 50 popular tools — things like Google Calendar, Calendly, Salesforce, HubSpot, QuickBooks, and more. If you use a booking system, a customer list, or even just a spreadsheet, we can usually hook into it. And if you use something unusual, we'll build a custom connection for you."
  },
  {
    question: "Who is Tara?",
    answer: "Tara is our AI assistant — think of her like a friendly receptionist who lives on your website. She can chat with visitors, answer questions about your services, help people book appointments, and collect contact info from potential customers — all day, every day, even at 3 AM. You can try talking to her right now using the chat button on this page."
  }
];

function FAQItem({ question, answer, isOpen, onClick, index }) {
  return (
    <motion.div
      className={`faq-item ${isOpen ? 'open' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <button
        className="faq-question"
        onClick={onClick}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
      >
        <span className="faq-question-text">{question}</span>
        <motion.span
          className="faq-icon"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <FaChevronDown />
        </motion.span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={`faq-answer-${index}`}
            className="faq-answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p>{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faq-section" id="faq">
      <div className="container">
        <motion.div
          className="faq-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="studio-eyebrow">A little clarity</span>
          <h2 className="faq-title">Frequently Asked Questions</h2>
          <p className="faq-subtitle">
            Got questions? We have answers. Here are the most common questions about our AI automation solutions.
          </p>
        </motion.div>

        <div className="faq-list">
          {faqData.map((item, index) => (
            <FAQItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index}
              onClick={() => toggleFAQ(index)}
              index={index}
            />
          ))}
        </div>

        <motion.div
          className="faq-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p>Still have questions?</p>
          <button
            className="faq-contact-button"
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Contact Us
          </button>
        </motion.div>
      </div>
    </section>
  );
}

export default FAQ;
